import React, { useEffect, useState } from "react";
import {
  IonModal,
  IonContent,
  IonButton,
  IonText,
} from "@ionic/react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { Capacitor } from "@capacitor/core";
import packageJson from "../package.json";

// Replace these with your actual store URLs
const APP_STORE_URL = "https://apps.apple.com/app/id6751321206";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.bldapps.bacolodai.md";

type GateType = "update" | "maintenance" | null;

export default function VersionGate() {
  const [gateType, setGateType] = useState<GateType>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, "config", "appVersion"));
      if (snap.exists()) {
        const data = snap.data();
        const localVersion = packageJson.version;

        // Check maintenance first
        if (data.maintenance === true) {
          setMessage(data.maintenanceMessage || "We're currently under maintenance.\nPlease check back later.");
          setGateType("maintenance");
          return;
        }

        // Then check version
        if (data.version !== localVersion) {
          setMessage(data.message || "A new version is available.\nPlease update to continue.");
          setGateType("update");
        }
      }
    })();
  }, []);

  const handleUpdate = () => {
    const platform = Capacitor.getPlatform();
    const url = platform === "ios" ? APP_STORE_URL : PLAY_STORE_URL;
    window.open(url, "_blank");
  };

  if (!gateType) return null;

  return (
    <IonModal isOpen={true} canDismiss={false} backdropDismiss={false}>
      <IonContent>
        {gateType === "update" && <UpdateScreen message={message} onUpdate={handleUpdate} />}
        {gateType === "maintenance" && <MaintenanceScreen message={message} />}
      </IonContent>
    </IonModal>
  );
}

function UpdateScreen({ message, onUpdate }: { message: string; onUpdate: () => void }) {
  return (
    <div style={styles.container}>
      <div style={styles.iconWrapper}>
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </div>
      <IonText>
        <h2 style={styles.title}>Update Available</h2>
      </IonText>
      <IonText>
        <p style={styles.message}>
          {message.split("\n").map((line, i) => (
            <span key={i}>{line}<br /></span>
          ))}
        </p>
      </IonText>
      <IonButton expand="block" onClick={onUpdate} style={styles.button}>
        Update Now
      </IonButton>
    </div>
  );
}

function MaintenanceScreen({ message }: { message: string }) {
  return (
    <div style={styles.container}>
      <div style={styles.iconWrapper}>
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
      </div>
      <IonText>
        <h2 style={styles.title}>Under Maintenance</h2>
      </IonText>
      <IonText>
        <p style={styles.message}>
          {message.split("\n").map((line, i) => (
            <span key={i}>{line}<br /></span>
          ))}
        </p>
      </IonText>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: "2em",
    background: "linear-gradient(135deg, #1a1a2e, #16213e)",
    textAlign: "center",
    gap: "1em",
  },
  iconWrapper: {
    background: "rgba(255,255,255,0.1)",
    borderRadius: "50%",
    padding: "1.2em",
    marginBottom: "0.5em",
  },
  title: {
    color: "white",
    fontSize: "1.6em",
    fontWeight: 700,
    margin: 0,
  },
  message: {
    color: "rgba(255,255,255,0.75)",
    fontSize: "1em",
    lineHeight: 1.6,
    margin: 0,
  },
  button: {
    marginTop: "1em",
    width: "100%",
  },
};